import { Stack } from '@mui/material'
import Paper from '@mui/material/Paper'
import type {
  DataGridProProps,
  GridApiPro,
  GridCellModesModel,
  GridCellParams,
  GridInitialState,
  GridRowId,
  GridRowParams,
  GridRowProps,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro'
import { DataGridPro, GridCellModes, GridRow } from '@mui/x-data-grid-pro'
import { useMemo, useRef, useState } from 'react'
import { useHref, useLinkClickHandler } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import DataGridSkeleton from '@/components/dataDisplay/DataGridSkeleton'
import {
  DEFAULT_DATA_GRID_PAGE_SIZE,
  DEFAULT_DATA_GRID_PAGE_SIZE_OPTIONS,
  DEFAULT_DATA_GRID_PAGINATION_MODEL,
} from '@/constants/datagrid'
import { useDataGridSyncUrlManager } from '@/contexts/DataGridSyncUrlManagerContext'
import { shouldShowPagination } from '@/utils/datagrid/pagination'
import { focusRow } from '@/utils/focusRow'

export interface CustomDataGridProps extends Omit<
  DataGridProProps,
  'onRowClick' | 'rowSelection' | 'rowSelectionModel' | 'loading'
> {
  autoHeight?: boolean
  includeWrapper?: boolean
  isLoading?: boolean
  clickableRows?: {
    isRowClickable?: (row: GridRowParams) => boolean
    navigateTo?: (row: GridRowParams) => string
    isRowSelectable?: (row: GridRowParams) => boolean
    onRowClick?: DataGridProProps['onRowClick']
    onRowSelectionModelChange?: DataGridProProps['onRowSelectionModelChange']
  }
  pageSize?: number
  rowSelection?: {
    multiple: boolean
    model: DataGridProProps['rowSelectionModel']
  }
  disableGutterTop?: boolean
  disableGutterBottom?: boolean
  disableBottomShadow?: boolean
  disableDefaultHeaderBackground?: boolean
  title?: string
  // This function will be used to determine if the row should be focused or not. When the row is focused, it will be highlighted.
  // It will work only if the pagination mode is set to "client". If the pagination mode is set to "server", the row will not be focused.
  getFocusedRow?: (rowId: GridRowId) => boolean
  // This element will be displayed on the right side of the title.
  titleExtraInfo?: JSX.Element
}

export const DEFAULT_FILTERS = {
  filter: {
    filterModel: {
      items: [],
      quickFilterValues: [],
    },
  },
}

const CustomDataGrid = ({
  includeWrapper = true,
  isLoading = false,
  slots,
  autoHeight = true,
  clickableRows,
  pageSizeOptions,
  sx,
  rowSelection,
  disableBottomShadow = false,
  disableGutterBottom = false,
  disableGutterTop = false,
  disableDefaultHeaderBackground = false,
  title,
  titleExtraInfo,
  getFocusedRow,
  ...dataGridProps
}: CustomDataGridProps) => {
  const resizeCounterRef = useRef(0)
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({})
  const syncUrlManager = useDataGridSyncUrlManager()
  // Default data grid props can be overridden by passing props to CustomDataGrid
  const defaultDataGridProps: Partial<DataGridProProps> = {
    autoHeight,
    hideFooterSelectedRowCount: true,
    rowSelection: false,
    density: 'standard',
  }
  const canSelectMultipleRows = rowSelection?.multiple ?? false
  const commonStyles = {
    p: 3,
    mb: disableGutterBottom ? 0 : 3,
    mt: disableGutterTop ? 0 : 3,
    boxShadow: disableBottomShadow ? 'none' : {},
  }
  const showToolbar = Boolean(slots?.toolbar)

  /**
   * To set the initial state of a grid, we take into account the information comming from the sync url manager.
   *
   * When there is not any initial state, we try to set it using the information from the search params.
   *
   * When there is an initial state, we use it as a default although the data coming from the data grid sync manager
   * takes priority.
   */
  const initialState: GridInitialState = useMemo(() => {
    if (!dataGridProps.initialState) {
      return {
        pagination: {
          paginationModel: syncUrlManager.initialState.paginationModel ?? DEFAULT_DATA_GRID_PAGINATION_MODEL,
        },
        filter: {
          filterModel: syncUrlManager.initialState.filterModel ?? undefined,
        },
        sorting: {
          sortModel: syncUrlManager.initialState.sortModel ?? undefined,
        },
      }
    }

    const defaultPaginationModel =
      dataGridProps.initialState.pagination?.paginationModel ?? DEFAULT_DATA_GRID_PAGINATION_MODEL

    return {
      ...dataGridProps.initialState,
      pagination: {
        ...dataGridProps.initialState.pagination,
        paginationModel: syncUrlManager.initialState.paginationModel ?? defaultPaginationModel,
      },
      filter: {
        ...dataGridProps.initialState.filter,
        filterModel: syncUrlManager.initialState.filterModel ?? dataGridProps.initialState.filter?.filterModel,
      },
      sorting: {
        ...dataGridProps.initialState.sorting,
        sortModel: syncUrlManager.initialState.sortModel ?? dataGridProps.initialState.sorting?.sortModel,
      },
    }
  }, [dataGridProps.initialState])

  function isRowClickable(params: GridRowParams) {
    if (!clickableRows) return false

    return clickableRows?.isRowClickable ? clickableRows.isRowClickable(params) : true
  }

  function isRowSelectable(params: GridRowParams) {
    if (!clickableRows) return false

    return clickableRows?.isRowSelectable ? clickableRows.isRowSelectable(params) : true
  }

  /**
   * the combination of handleCellClick and handleCellModesModelChange makes possible to open edit mode of a cell
   * with just one click. the default behaviour of the DataGrid is to open edit mode only after double click.
   */
  function handleCellClick(params: GridCellParams) {
    if (!params.colDef.editable || !params.isEditable) return

    setCellModesModel((prevModel) => {
      return {
        // Revert the mode of the other cells from other rows
        ...Object.keys(prevModel).reduce(
          (acc, id) => ({
            ...acc,
            [id]: Object.keys(prevModel[id]).reduce(
              (acc2, field) => ({
                ...acc2,
                [field]: { mode: GridCellModes.View },
              }),
              {},
            ),
          }),
          {},
        ),
        [params.id]: {
          // Revert the mode of other cells in the same row
          ...Object.keys(prevModel[params.id] || {}).reduce(
            (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
            {},
          ),
          [params.field]: { mode: GridCellModes.Edit },
        },
      }
    })
  }

  function handleCellModesModelChange(newModel: GridCellModesModel) {
    setCellModesModel(newModel)
  }

  /**
   * We consider that the DataGrid is rendered when the onResize callback is called at least once. The first
   * time is called it means that all elements are visible on the screen.
   *
   * This is useful when you need to take an action on the DOM element, as you can be sure that the rows
   * are already visible on the screen.
   *
   */
  function handleDataGridRendered(gridApi: GridApiPro) {
    if (getFocusedRow) {
      focusRow(gridApi, getFocusedRow)
    }
  }

  if (isLoading) {
    return (
      <DataGridSkeleton
        includeWrapper={includeWrapper}
        numberOfColumns={dataGridProps.columns?.length ?? 0}
        numberOfRows={DEFAULT_DATA_GRID_PAGE_SIZE}
        sx={commonStyles}
        withToolbar={Boolean(slots?.toolbar)}
      />
    )
  }

  const rowSlot = clickableRows?.navigateTo
    ? {
        row: (params) => <NavigatingRow to={clickableRows.navigateTo!(params)} {...params} />,
      }
    : {}

  const Content = (
    <>
      <Stack sx={{ flexDirection: 'row', gap: 2, justifyContent: 'space-between' }}>
        {title && (
          <CustomTypography gutterBottom variant="h5">
            {title}
          </CustomTypography>
        )}
        {titleExtraInfo}
      </Stack>

      <DataGridPro
        {...defaultDataGridProps}
        {...dataGridProps}
        disableColumnFilter
        disableColumnMenu
        cellModesModel={cellModesModel}
        getRowClassName={(params) =>
          [isRowClickable(params) ? 'clickable' : 'non-clickable', dataGridProps.getRowClassName?.(params) || '']
            .filter(Boolean)
            .join(' ')
        }
        initialState={initialState}
        isRowSelectable={(params) => isRowSelectable(params) || false}
        loading={isLoading}
        pageSizeOptions={pageSizeOptions ?? DEFAULT_DATA_GRID_PAGE_SIZE_OPTIONS}
        pagination={shouldShowPagination({ dataGridProps, initialState })}
        rowSelection={getFocusedRow ? true : Boolean(rowSelection)}
        rowSelectionModel={rowSelection?.model}
        showToolbar={showToolbar}
        slots={{
          ...slots,
          ...rowSlot,
        }}
        sx={{
          ...sx,
          bgcolor: 'transparent',
          border: 'none',
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none !important;',
          },
          '& .MuiDataGrid-row.clickable:hover': {
            cursor: 'pointer',
            backgroundColor: 'rgba(20, 132, 160, 0.04)',
          },
          '& .MuiDataGrid-row.non-clickable': {
            cursor: 'default',
            backgroundColor: 'inherit',
          },
          '& .MuiDataGrid-row--detailPanelExpanded': {
            background: 'rgba(20, 132, 160, 0.08)',
          },
          '& .MuiDataGrid-columnHeader': {
            background: disableDefaultHeaderBackground ? '' : 'white',
          },
          '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
            display: canSelectMultipleRows ? null : 'none',
          },
          '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
            alignItems: 'center',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            display: 'flex',
            padding: '0px 10px',
          },
          '& .MuiDataGrid-detailPanel': {
            background: 'rgba(20, 132, 160, 0.04)',
          },
          '& .MuiDataGrid-filler': {
            backgroundColor: 'white',
          },
          '& .MuiDataGrid-toolbar': {
            border: 'none',
          },
        }}
        onCellClick={handleCellClick}
        onCellModesModelChange={handleCellModesModelChange}
        onFilterModelChange={(filterModel, ...args) => {
          syncUrlManager.syncFilterSearchParams(filterModel)
          dataGridProps.onFilterModelChange?.(filterModel, ...args)

          // When pagination is enabled, reset the page to 0 when filters change
          if (!dataGridProps.hideFooterPagination) {
            const { api } = args[0]

            api.setPage(0)
          }
        }}
        onPaginationModelChange={(paginationModel, ...args) => {
          syncUrlManager.syncPaginationSearchParams(paginationModel)
          dataGridProps.onPaginationModelChange?.(paginationModel, ...args)
        }}
        onResize={(...args) => {
          const { api } = args[2]

          if (resizeCounterRef.current === 0) {
            handleDataGridRendered(api as GridApiPro)
            resizeCounterRef.current += 1
          }
        }}
        onRowClick={(params, ...rest) => {
          if (!clickableRows || !isRowClickable(params) || !clickableRows.onRowClick) return

          clickableRows.onRowClick(params, ...rest)
        }}
        onRowSelectionModelChange={(selectedModels, details) => {
          if (!clickableRows?.onRowSelectionModelChange) return

          function getLastModel(): GridRowSelectionModel {
            return selectedModels.ids.size > 0
              ? { type: selectedModels.type, ids: new Set([Array.from(selectedModels.ids).pop() as GridRowId]) }
              : { type: 'include', ids: new Set() }
          }

          // If multiple selection is not allowed, only the last selected model will be passed to the callback
          clickableRows.onRowSelectionModelChange(canSelectMultipleRows ? selectedModels : getLastModel(), details)
        }}
        onSortModelChange={(sortModel, ...args) => {
          syncUrlManager.syncSortSearchParams(sortModel)
          dataGridProps.onSortModelChange?.(sortModel, ...args)
        }}
      />
    </>
  )

  if (includeWrapper) {
    return <Paper sx={commonStyles}>{Content}</Paper>
  }

  return Content
}

type NavigatingRowProps = GridRowProps & { to: string }

function NavigatingRow({ to, ...rowProps }: Readonly<NavigatingRowProps>) {
  const href = useHref(to)
  const handleLinkClick = useLinkClickHandler(to)

  return (
    <a
      href={href}
      onClick={(event) => {
        // Like Link, let cell buttons opt out with preventDefault. Portaled content in a cell (e.g. a dialog)
        // also bubbles its clicks here without being inside the row, so ignore those too.
        if (!event.defaultPrevented && event.currentTarget.contains(event.target as Node)) {
          handleLinkClick(event)
        }
      }}
    >
      <GridRow {...rowProps} />
    </a>
  )
}

export default CustomDataGrid
